import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import useGeocoding from '../../hooks/vehicle/useGeocoding';

// Iconos
import { ReactComponent as LocationIcon } from '../../assets/svg/maps/location.svg';
import { ReactComponent as LoadingIcon } from '../../assets/svg/view/loading.svg';
import { ReactComponent as RefreshIcon } from '../../assets/svg/view/refresh.svg';
import { ReactComponent as ErrorIcon } from '../../assets/svg/layout/flag.svg';

const ClickableLocation = ({ 
  latitude, 
  longitude, 
  initialAddress = null,
  vehicleId = null,
  theme = 'light',
  className = '',
  showCoordinates = false,
  maxLength = 50,
  onAddressUpdate = null
}) => {
  const [currentAddress, setCurrentAddress] = useState(initialAddress);
  const [hasError, setHasError] = useState(false);
  const { geocodeLocation, isLoading, cancelGeocoding } = useGeocoding();
  
  const locationKey = `${latitude}_${longitude}`;
  const loading = isLoading(latitude, longitude);

  // Verificar si la ubicación está disponible
  const isLocationAvailable = Boolean(
    currentAddress && 
    currentAddress !== 'Ubicación no disponible' && 
    currentAddress !== 'Error obteniendo ubicación' &&
    !currentAddress.includes('Error')
  );

  // 🎯 Manejar click para geocodificar
  const handleGeocode = useCallback(async (event) => {
    event.stopPropagation();
    
    if (loading) {
      cancelGeocoding(latitude, longitude);
      return;
    }

    if (!latitude || !longitude || isNaN(parseFloat(latitude)) || isNaN(parseFloat(longitude))) {
      console.warn('⚠️ Coordenadas inválidas para geocodificación');
      return;
    }

    setHasError(false);
    
    const result = await geocodeLocation(latitude, longitude, vehicleId);
    
    if (result) {
      if (result.success) {
        setCurrentAddress(result.address);
        setHasError(false);
        
        // Notificar al componente padre si hay callback
        if (onAddressUpdate) {
          onAddressUpdate({
            vehicleId,
            address: result.address,
            coordinates: { latitude, longitude },
            source: result.source
          });
        }
      } else {
        setCurrentAddress(result.address || 'Error obteniendo ubicación');
        setHasError(true);
      }
    }
  }, [latitude, longitude, vehicleId, loading, geocodeLocation, cancelGeocoding, onAddressUpdate]);

  // Efecto para actualizar dirección cuando cambie la prop
  useEffect(() => {
    if (initialAddress !== currentAddress && initialAddress) {
      setCurrentAddress(initialAddress);
      setHasError(false);
    }
  }, [initialAddress]);

  // 🎨 Determinar estado visual
  const getDisplayState = () => {
    if (loading) return 'loading';
    if (hasError) return 'error';
    if (isLocationAvailable) return 'available';
    return 'unavailable';
  };

  // 🎨 Formatear texto mostrado
  const getDisplayText = () => {
    if (loading) return 'Obteniendo ubicación...';
    
    if (!currentAddress || currentAddress === 'Ubicación no disponible') {
      return 'Ubicación no disponible';
    }

    if (currentAddress.includes('Error')) {
      return 'Error obteniendo ubicación';
    }

    // Truncar si es muy largo
    if (currentAddress.length > maxLength) {
      return `${currentAddress.substring(0, maxLength)}...`;
    }

    return currentAddress;
  };

  // 🎨 Obtener ícono según estado
  const getIcon = () => {
    const iconClass = `GM__${theme}-location-icon`;
    
    switch (getDisplayState()) {
      case 'loading':
        return <LoadingIcon className={`${iconClass} spinning`} />;
      case 'error':
        return <ErrorIcon className={`${iconClass} error`} />;
      case 'available':
        return <LocationIcon className={`${iconClass} available`} />;
      default:
        return <RefreshIcon className={`${iconClass} clickable`} />;
    }
  };

  // 🎨 Obtener título para tooltip
  const getTitle = () => {
    const state = getDisplayState();
    
    switch (state) {
      case 'loading':
        return loading ? 'Click para cancelar geocodificación' : 'Obteniendo ubicación...';
      case 'error':
        return 'Error obteniendo ubicación. Click para reintentar';
      case 'available':
        return `${currentAddress}${showCoordinates ? ` (${latitude}, ${longitude})` : ''}`;
      default:
        return 'Click para obtener dirección exacta';
    }
  };

  const displayState = getDisplayState();
  const canClick = displayState !== 'loading' && (displayState === 'unavailable' || displayState === 'error');

  return (
    <div className={`GM__${theme}-clickable-location ${className}`}>
      <div 
        className={`GM__${theme}-location-container ${displayState} ${canClick ? 'clickable' : ''}`}
        onClick={canClick ? handleGeocode : undefined}
        title={getTitle()}
        role={canClick ? 'button' : 'text'}
        tabIndex={canClick ? 0 : -1}
        onKeyDown={(e) => {
          if (canClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleGeocode(e);
          }
        }}
      >
        {getIcon()}
        
        <span className={`GM__${theme}-location-text ${displayState}`}>
          {getDisplayText()}
        </span>
        
        {canClick && (
          <span className={`GM__${theme}-click-hint`}>
            {displayState === 'error' ? 'Reintentar' : 'Obtener'}
          </span>
        )}
      </div>
      
      {showCoordinates && (
        <div className={`GM__${theme}-coordinates-info`}>
          <small className={`GM__${theme}-coordinates-text`}>
            {parseFloat(latitude).toFixed(6)}, {parseFloat(longitude).toFixed(6)}
          </small>
        </div>
      )}
    </div>
  );
};

ClickableLocation.propTypes = {
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialAddress: PropTypes.string,
  vehicleId: PropTypes.string,
  theme: PropTypes.string,
  className: PropTypes.string,
  showCoordinates: PropTypes.bool,
  maxLength: PropTypes.number,
  onAddressUpdate: PropTypes.func
};

export default ClickableLocation;